import Button from "@/components/base/Button";
import PageHero from "@/components/base/PageHero";
import PhotoPlaceholder from "@/components/base/PhotoPlaceholder";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import { Link } from "react-router-dom";
import { useUi } from "@/context/UiContext";
import Seo from "@/components/base/Seo";
import { personSchema } from "@/lib/seo";

const PRINCIPLES = [
  "先理解，再行動",
  "從做得到的地方開始",
  "讓行動留下回饋",
  "建立自己的方法",
  "讓成長產生影響",
];

const TIMELINE = [
  "工程／技術",
  "企業管理",
  "顧問／改善",
  "人才培育",
  "講師／培訓",
  "幸福人才",
];

const GALLERY = ["企業培訓", "公開講座", "工作坊", "學員互動", "顧問現場"];

export default function About() {
  const { openQuiz } = useUi();

  return (
    <>
      <Seo
        title="許文彰｜幸福人才總教練｜企業管理・人才培育・個人成長"
        description="許文彰（幸福人才總教練）整合 30 年以上工程、企業管理、問題診斷、流程改善與人才培育經驗，從解決企業問題到陪伴個人找到能持續的改變。"
        keywords="許文彰,幸福人才總教練,企業管理,人才培育,個人成長"
        jsonLd={personSchema}
      />
      <PageHero
        eyebrow="關於許文彰"
        title={
          <>
            從解決企業的問題，
            <br />
            到陪人找到真正能持續的改變。
          </>
        }
        description={
          <>
            <p>
              我是許文彰。多年來，我從工程、企業管理、問題診斷、流程改善到人才培育，
              一直習慣問：真正影響結果的關鍵到底是什麼？
            </p>
            <p>
              後來我發現，企業的問題與一個人的成長，其實有很多相似之處。
              知道，並不代表做得到；開始，也不代表能持續。
              這也成為我整理「幸福人才養成系統」的起點。
            </p>
          </>
        }
      >
        <Button to="/happiness-talent-system" icon="ri-arrow-right-line">
          了解幸福人才養成系統
        </Button>
      </PageHero>

      {/* 我的起點 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="我的起點"
              title="我的起點，不是心靈成長，而是解決問題。"
            />
            <div className="mt-6 space-y-5 text-base leading-[1.85] text-ink-soft reading-width">
              <p>
                工程與管理背景，讓我很早就習慣用系統性的方式看事情。一個結果不好，
                不能只看最後發生了什麼。還要回頭看：流程在哪裡出了問題？
                資訊在哪裡斷掉？人的行為為什麼沒有發生？方法是否真的支持想要的結果？
              </p>
              <p>
                這樣的思考方式，後來一路延伸到企業管理、流程改善、問題診斷、
                人才培育與教育訓練。
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 從企業問題看見人的問題 */}
      <section className="bg-cream">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="從企業到人"
              title="系統可以改善流程，但真正讓系統運作的，始終是人。"
            />
            <div className="mt-6 space-y-5 text-base leading-[1.85] text-ink-soft reading-width">
              <p>在企業管理與人才培育過程中，我越來越常看到：</p>
              <ul className="space-y-2">
                <li>制度訂了，沒有執行。</li>
                <li>目標設定了，沒有持續。</li>
                <li>課程上完了，回到工作現場又恢復原樣。</li>
                <li>主管知道方法，真正遇到問題卻還是回到原來的習慣。</li>
              </ul>
              <p>
                這跟我們每個人的生活其實很像。所以我開始把注意力從「事情怎麼做」
                進一步放到「人為什麼做不到」。
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 為什麼是幸福人才 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="為什麼是幸福人才"
              title="人才，不只是企業裡的一個角色。"
            />
            <div className="mt-6 text-base leading-[1.85] text-ink-soft reading-width">
              <p>
                我心中的「幸福人才」，不只是能力很好、績效很好。更重要的是：
                知道自己真正重視什麼；知道自己想成為什麼樣的人；
                能把理解逐漸變成行動；也能把自己的能力，用在真正重要的地方。
              </p>
            </div>
            <div className="mt-8 rounded-card bg-primary-dark p-8 md:p-10">
              <p className="text-xl md:text-2xl font-bold text-white leading-[1.5]">
                幸福人才，是有能力活出自己真正重視的人。
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 我的理念 */}
      <section className="bg-cream">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="我的理念"
              title="不追求一次巨大的改變，而是建立可以持續的小改變。"
            />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p} delay={i * 70}>
                <div className="h-full rounded-card bg-white border border-line p-6 transition-transform duration-300 hover:-translate-y-1">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gold text-primary-dark text-sm font-bold">
                    {i + 1}
                  </div>
                  <p className="mt-4 text-base font-semibold text-ink">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 專業歷程 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="專業歷程" title="一路走來，始終在做同一件事。" />
          </Reveal>
          <div className="mt-10">
            {TIMELINE.map((t, i) => (
              <Reveal key={t} delay={i * 60}>
                <div className="flex gap-5">
                  <div className="flex flex-col items-center">
                    <div className="w-4 h-4 rounded-full bg-gold shrink-0"></div>
                    {i < TIMELINE.length - 1 && (
                      <div className="w-px flex-1 bg-line"></div>
                    )}
                  </div>
                  <div className="pb-10">
                    <p className="text-lg font-bold text-ink">{t}</p>
                    <p className="mt-1 text-sm text-ink-soft/60">
                      年份／經歷細節待補
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 專業與現場 */}
      <section className="bg-cream">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="專業與現場" title="在工作現場，看見真正發生的改變。" />
          </Reveal>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {GALLERY.map((g, i) => (
              <Reveal key={g} delay={i * 60}>
                <PhotoPlaceholder label={g} className="aspect-square" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-primary-dark">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-2xl md:text-[2.125rem] font-bold leading-[1.35] text-white">
                從看見卡點，到真正能夠持續。
              </h2>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <Button to="/happiness-talent-system" icon="ri-arrow-right-line">
                  了解幸福人才養成系統
                </Button>
                <Button onClick={openQuiz} variant="outline-light">
                  30 秒找出我的行動卡點
                </Button>
              </div>
              <Link
                to="/contact"
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-gold cursor-pointer transition-colors duration-200 hover:text-gold-soft"
              >
                講師／企業合作邀約
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}